import React, { useEffect, useState, useContext } from 'react';
import api from '../api/axios';
import templatesApi from '../api/templatesApi';
import { AuthContext } from '../AuthContext';
import { 
  Box, 
  Typography, 
  Button, 
  TextField, 
  List, 
  ListItem, 
  ListItemText, 
  IconButton, 
  CircularProgress, 
  Alert, 
  Card, 
  CardContent, 
  CardActions, 
  Grid, 
  Chip, 
  Avatar,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import { useNavigate, useParams } from 'react-router-dom';
import BreadcrumbsNav from '../components/BreadcrumbsNav';
import SyncDialog from '../components/SyncDialog';

const DashboardPage = () => {
  const { user, logout } = useContext(AuthContext);
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [company, setCompany] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [ownedTemplates, setOwnedTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);
  const [showTemplateSettingsDialog, setShowTemplateSettingsDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplateSettings, setSavingTemplateSettings] = useState(false);
  const [templateSettings, setTemplateSettings] = useState({
    name: '',
    description: '',
    category: 'custom',
    tags: '',
    availability: 'current_company',
    sharedCompanyIds: []
  });
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Duplicate project state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [projectToDuplicate, setProjectToDuplicate] = useState(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [duplicating, setDuplicating] = useState(false);

  // Sync state
  const [showSyncDialog, setShowSyncDialog] = useState(false);

  // Settings state
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [settingsName, setSettingsName] = useState('');
  const [settingsDescription, setSettingsDescription] = useState('');
  const [settingsIndustry, setSettingsIndustry] = useState('');
  const [settingsPrimaryColor, setSettingsPrimaryColor] = useState('#667eea');
  const [settingsActionColor, setSettingsActionColor] = useState('#667eea');
  const [useMonochrome, setUseMonochrome] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  const industries = [
    'Technology', 'Healthcare', 'Finance', 'Education', 'Manufacturing', 
    'Retail', 'Consulting', 'Real Estate', 'Legal', 'Non-Profit', 'Other'
  ];

  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch company info first
      try {
        const companyRes = await api.get(`/companies/${companyId}`);
        setCompany(companyRes.data);
      } catch (companyErr) {
        console.log('Company endpoint not yet implemented:', companyErr.message);
        // Set generic company data
        setCompany({
          _id: companyId,
          name: `Company ${companyId.slice(-6)}`,
          plan: "free",
          description: "Company description not available",
          primaryColor: '#667eea',
          industry: 'General'
        });
      }

      try {
        const companiesRes = await api.get('/companies');
        setAllCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
      } catch (companiesErr) {
        console.log('Companies endpoint not available for template management:', companiesErr.message);
        setAllCompanies([]);
      }

      // Fetch projects for this specific company
      try {
        const projectsRes = await api.get(`/companies/${companyId}/projects`);
        console.log('Company projects API response:', projectsRes.data);
        setProjects(Array.isArray(projectsRes.data) ? projectsRes.data : []);
      } catch (projectsErr) {
        console.log('Company projects endpoint not yet implemented, showing empty state');
        setProjects([]);
      }

      // Fetch saved flow templates for project creation.
      try {
        setTemplatesLoading(true);
        const savedTemplates = await templatesApi.listTemplates(companyId);
        const userTemplates = await templatesApi.listTemplates(companyId, { ownedOnly: true });
        setTemplates(savedTemplates);
        setOwnedTemplates(userTemplates);
      } catch (templatesErr) {
        console.log('Flow templates endpoint not available:', templatesErr.message);
        setTemplates([]);
        setOwnedTemplates([]);
      } finally {
        setTemplatesLoading(false);
      }
    } catch (err) {
      setError('Failed to load company dashboard');
      console.error('Dashboard error:', err);
    }
    setLoading(false);
  };

  // Calculate stats whenever projects change
  useEffect(() => {
    if (projects.length >= 0) {
      const totalExecutions = projects.reduce((sum, p) => sum + (p.stats?.totalExecutions || 0), 0);
      setStats({
        totalProjects: projects.length,
        activeProjects: projects.filter(p => p.status === 'active').length,
        totalExecutions,
        thisMonthExecutions: Math.floor(totalExecutions * 0.3)
      });
    }
  }, [projects]);

  useEffect(() => {
    if (companyId) {
      fetchDashboardData();
    }
  }, [companyId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      await api.post(`/companies/${companyId}/projects`, { 
        name, 
        description,
        companyId,
        templateId: selectedTemplateId || undefined
      });
      setName('');
      setDescription('');
      setSelectedTemplateId('');
      setShowCreateForm(false);
      fetchDashboardData();
    } catch (err) {
      setError('Failed to create project');
    }
    setCreating(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project? This action cannot be undone.')) return;
    try {
      await api.delete(`/companies/${companyId}/projects/${id}`);
      setProjects(projects.filter(p => p._id !== id));
      fetchDashboardData(); // Refresh stats
    } catch {
      setError('Failed to delete project');
    }
  };

  const handleDuplicateClick = (project) => {
    setProjectToDuplicate(project);
    setDuplicateName(`${project.name} (Copy)`);
    setShowDuplicateModal(true);
  };

  const handleDuplicate = async () => {
    if (!projectToDuplicate || !duplicateName.trim()) return;
    
    setDuplicating(true);
    setError('');
    try {
      await api.post(`/companies/${companyId}/projects/${projectToDuplicate._id}/duplicate`, {
        name: duplicateName.trim()
      });
      setShowDuplicateModal(false);
      setProjectToDuplicate(null);
      setDuplicateName('');
      fetchDashboardData(); // Refresh the project list
    } catch (err) {
      setError('Failed to duplicate project');
      console.error('Duplicate error:', err);
    }
    setDuplicating(false);
  };

  const handleCloseDuplicateModal = () => {
    setShowDuplicateModal(false);
    setProjectToDuplicate(null);
    setDuplicateName('');
    setError('');
  };

  const handleSyncCompany = () => {
    setShowSyncDialog(true);
  };

  const handleSyncComplete = (result) => {
    console.log('Company sync completed:', result);
    // Optionally refresh data or show success message
    fetchDashboardData();
  };

  const handleOpenSettings = () => {
    if (company) {
      setSettingsName(company.name || '');
      setSettingsDescription(company.description || '');
      setSettingsIndustry(company.industry || '');
      setSettingsPrimaryColor(company.primaryColor || '#667eea');
      setSettingsActionColor(company.branding?.actionColor || company.primaryColor || '#667eea');
      setUseMonochrome(!company.branding?.actionColor || company.branding?.actionColor === company.primaryColor);
      setShowSettingsDialog(true);
    }
  };

  const handleCloseSettings = () => {
    setShowSettingsDialog(false);
    setSettingsName('');
    setSettingsDescription('');
    setSettingsIndustry('');
    setSettingsPrimaryColor('#667eea');
    setSettingsActionColor('#667eea');
    setUseMonochrome(true);
    setError('');
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    setError('');
    try {
      const updateData = {
        name: settingsName,
        description: settingsDescription,
        industry: settingsIndustry,
        branding: {
          primaryColor: settingsPrimaryColor,
          actionColor: useMonochrome ? settingsPrimaryColor : settingsActionColor
        }
      };
      
      console.log('Updating company:', companyId, updateData);
      const response = await api.put(`/companies/${companyId}`, updateData);
      
      // Update the local company state
      setCompany({
        ...response.data,
        primaryColor: response.data.settings?.theme?.primaryColor || settingsPrimaryColor,
        branding: response.data.branding || updateData.branding
      });
      
      setShowSettingsDialog(false);
      handleCloseSettings();
      fetchDashboardData(); // Refresh data
    } catch (err) {
      console.error('Update company error:', err);
      setError(err.response?.data?.message || 'Failed to update company settings');
    }
    setSavingSettings(false);
  };

  const handleDeleteCompany = async () => {
    if (!window.confirm('Delete this company? This will also delete all associated projects. This action cannot be undone.')) return;
    
    try {
      console.log('Deleting company:', companyId);
      await api.delete(`/companies/${companyId}`);
      // Navigate back to company selection
      navigate('/');
    } catch (err) {
      console.error('Delete company error:', err);
      setError(err.response?.data?.message || 'Failed to delete company');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'draft': return 'warning';
      case 'inactive': return 'default';
      case 'archived': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <PlayArrowIcon fontSize="small" />;
      case 'draft': return <EditIcon fontSize="small" />;
      case 'inactive': return <SettingsIcon fontSize="small" />;
      default: return <SettingsIcon fontSize="small" />;
    }
  };

  const handleShowCreateForm = () => setShowCreateForm(true);
  const handleHideCreateForm = () => {
    setShowCreateForm(false);
    setName('');
    setDescription('');
    setSelectedTemplateId('');
    setError('');
  };

  const getCompanyName = (id) => {
    const foundCompany = allCompanies.find(companyItem => companyItem._id === id);
    return foundCompany?.name || 'Unknown company';
  };

  const getAvailabilityLabel = (template) => {
    if (template.availability === 'all_companies' || template.isPublic) return 'All companies';
    if (template.availability === 'selected_companies' || template.sharedCompanyIds?.length) {
      return `Selected companies (${template.sharedCompanyIds?.length || 0})`;
    }
    return 'Source company only';
  };

  const handleEditTemplateClick = (template) => {
    setEditingTemplate(template);
    setTemplateSettings({
      name: template.name || '',
      description: template.description || '',
      category: template.category || 'custom',
      tags: (template.tags || []).join(', '),
      availability: template.availability || (template.isPublic ? 'all_companies' : (template.sharedCompanyIds?.length ? 'selected_companies' : 'current_company')),
      sharedCompanyIds: (template.sharedCompanyIds || []).map(companyIdValue => companyIdValue.toString())
    });
    setShowTemplateSettingsDialog(true);
    setError('');
  };

  const handleCloseTemplateSettings = () => {
    setShowTemplateSettingsDialog(false);
    setEditingTemplate(null);
    setTemplateSettings({
      name: '',
      description: '',
      category: 'custom',
      tags: '',
      availability: 'current_company',
      sharedCompanyIds: []
    });
  };

  const handleSaveTemplateSettings = async () => {
    if (!editingTemplate) return;

    setSavingTemplateSettings(true);
    setError('');
    try {
      await templatesApi.updateTemplate(editingTemplate._id, {
        ...templateSettings,
        tags: templateSettings.tags
          .split(',')
          .map(tag => tag.trim())
          .filter(Boolean)
      });
      handleCloseTemplateSettings();
      await fetchDashboardData();
    } catch (err) {
      console.error('Failed to update template:', err);
      setError(err.response?.data?.message || 'Failed to update template');
    } finally {
      setSavingTemplateSettings(false);
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"? This action cannot be undone.`)) return;

    setError('');
    try {
      await templatesApi.deleteTemplate(template._id);
      await fetchDashboardData();
    } catch (err) {
      console.error('Failed to delete template:', err);
      setError(err.response?.data?.message || 'Failed to delete template');
    }
  };

  return (
    <Box 
      sx={{
        bgcolor: '#f5f5f5',
        minHeight: '100vh',
        width: '100%',
        '& .MuiPaper-root:not([class*="MuiPaper-elevation1"])': {
          bgcolor: '#ffffff !important',
        },
        '& .MuiCard-root': {
          bgcolor: '#ffffff !important',
        },
        '& .MuiTableContainer-root': {
          bgcolor: '#ffffff !important',
        }
      }}
    >
      <Box 
        maxWidth={1200} 
        mx="auto" 
        mt={4} 
        px={3}
        py={3}
      >
      <BreadcrumbsNav />
      
              {/* Company Header */}
      {company && (
        <Paper 
          elevation={1} 
          sx={{ 
            p: 3, 
            mb: 4, 
            background: `linear-gradient(135deg, ${company.primaryColor || '#667eea'} 0%, #764ba2 100%)`, 
            color: 'white' 
          }}
        >
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', width: 60, height: 60 }}>
                <BusinessIcon fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {company.name}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {company.description}
                </Typography>
                <Box display="flex" gap={1} mt={1}>
                  {/* Plan display removed */}
                  {company.industry && (
                    <Chip 
                      label={company.industry}
                      size="small" 
                      variant="outlined"
                      sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={1} width={{ xs: '100%', md: 'auto' }}>
              <Button
                variant="contained"
                startIcon={<CloudSyncIcon />}
                onClick={handleSyncCompany}
                fullWidth={{ xs: true, md: false }}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)'
                  }
                }}
              >
                Sync to Production
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/')}
                fullWidth={{ xs: true, md: false }}
                sx={{ 
                  color: 'white', 
                  borderColor: 'rgba(255,255,255,0.3)',
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.5)',
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                ← Back to Companies
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Dashboard Stats */}
      {stats && (
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <AccountTreeIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.totalProjects}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Projects
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <PlayArrowIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.activeProjects}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Active Projects
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'info.main' }}>
                    <AssessmentIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.totalExecutions}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Executions
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'warning.main' }}>
                    <AssessmentIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.thisMonthExecutions}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      This Month
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Quick Actions Section */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': { 
                transform: 'translateY(-2px)',
                boxShadow: 4
              }
            }}
            onClick={() => navigate(`/company/${companyId}/client-users`)}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: '#667eea', width: 56, height: 56 }}>
                  <PeopleIcon fontSize="large" />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Manage Client Users
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Invite and manage client users, set permissions, and track access
                  </Typography>
                </Box>
              </Box>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/company/${companyId}/client-users`);
                }}
              >
                Manage Users
              </Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': { 
                transform: 'translateY(-2px)',
                boxShadow: 4
              }
            }}
            onClick={handleOpenSettings}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: '#764ba2', width: 56, height: 56 }}>
                  <SettingsIcon fontSize="large" />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Company Settings
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Configure branding, industry, and company preferences
                  </Typography>
                </Box>
              </Box>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenSettings();
                }}
              >
                Manage Settings
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Flow Templates Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight="bold">
            Flow Templates
          </Typography>
          {templatesLoading && <CircularProgress size={20} />}
        </Box>

        {ownedTemplates.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No saved templates yet. Open a flow and use “Save as Template” to create one.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {ownedTemplates.map(template => (
              <Grid size={{ xs: 12, md: 6 }} key={template._id}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          {template.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {template.description || 'No description'}
                        </Typography>
                      </Box>
                      <Chip label={getAvailabilityLabel(template)} size="small" />
                    </Box>
                    <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                      <Chip label={`${template.stepCount || 0} steps`} size="small" variant="outlined" />
                      <Chip label={template.category || 'custom'} size="small" variant="outlined" />
                      {template.sharedCompanyIds?.map(companyIdValue => (
                        <Chip
                          key={companyIdValue}
                          label={getCompanyName(companyIdValue.toString())}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditTemplateClick(template)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteTemplate(template)}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Projects Section */}
      <Paper elevation={1} sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight="bold">
            Your Projects
          </Typography>
          {!showCreateForm && (
            <Button variant="contained" onClick={handleShowCreateForm} startIcon={<AccountTreeIcon />}>
              Create Project
            </Button>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Create Form */}
        {showCreateForm && (
          <Paper elevation={2} sx={{ p: 3, mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
            <Typography variant="h6" mb={2}>Create New Project</Typography>
            <Box component="form" onSubmit={handleCreate} display="flex" flexDirection="column" gap={2}>
              <TextField 
                label="Project Name" 
                value={name} 
                onChange={e => setName(e.target.value)} 
                required 
                fullWidth
              />
              <TextField 
                label="Description" 
                value={description} 
                onChange={e => setDescription(e.target.value)} 
                multiline
                rows={3}
                fullWidth
              />
              <TextField
                select
                label="Start from template"
                value={selectedTemplateId}
                onChange={e => setSelectedTemplateId(e.target.value)}
                fullWidth
                helperText={templatesLoading ? 'Loading templates...' : 'Optional: choose a saved flow template'}
                disabled={templatesLoading || creating}
              >
                <MenuItem value="">Blank project</MenuItem>
                {templates.map(template => (
                  <MenuItem key={template._id} value={template._id}>
                    {template.name} ({template.stepCount || 0} steps)
                  </MenuItem>
                ))}
              </TextField>
              <Box display="flex" gap={2}>
                <Button type="submit" variant="contained" disabled={creating}>
                  {creating ? 'Creating...' : 'Create Project'}
                </Button>
                <Button variant="outlined" onClick={handleHideCreateForm}>
                  Cancel
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Projects Grid */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {Array.isArray(projects) && projects.map(project => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={project._id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3
                    }
                  }}
                  onClick={() => navigate(`/company/${companyId}/project/${project._id}`)}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Typography variant="h6" fontWeight="bold" noWrap>
                        {project.name}
                      </Typography>
                      <Chip 
                        label={project.status || 'draft'} 
                        color={getStatusColor(project.status || 'draft')}
                        icon={getStatusIcon(project.status || 'draft')}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2, minHeight: '3em' }}>
                      {project.description || 'No description provided'}
                    </Typography>
                    
                    {/* Project Stats */}
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="textSecondary">
                        Executions: {project.stats?.totalExecutions || 0}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {project.isPublished ? 'Published' : 'Draft'}
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/company/${companyId}/project/${project._id}`);
                      }}
                    >
                      Open Flow
                    </Button>
                    <Button 
                      size="small" 
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicateClick(project);
                      }}
                      startIcon={<FileCopyIcon />}
                    >
                      Duplicate
                    </Button>
                    <Button 
                      size="small" 
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(project._id);
                      }}
                      startIcon={<DeleteIcon />}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
            
            {/* Empty State */}
            {projects.length === 0 && (
              <Grid item xs={12}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 6, 
                    textAlign: 'center', 
                    border: '2px dashed', 
                    borderColor: 'grey.300',
                    bgcolor: 'grey.50'
                  }}
                >
                  <AccountTreeIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="textSecondary" mb={1}>
                    No projects yet
                  </Typography>
                  <Typography variant="body2" color="textSecondary" mb={3}>
                    Create your first data extraction workflow to get started
                  </Typography>
                  <Button variant="contained" onClick={handleShowCreateForm} startIcon={<AccountTreeIcon />}>
                    Create Your First Project
                  </Button>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}
      </Paper>

      {/* Duplicate Project Modal */}
      <Dialog open={showDuplicateModal} onClose={handleCloseDuplicateModal} maxWidth="sm" fullWidth>
        <DialogTitle>Duplicate Project</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {projectToDuplicate && `Create a copy of "${projectToDuplicate.name}" with all its flow steps and configurations.`}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="New Project Name"
            type="text"
            fullWidth
            variant="outlined"
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
            disabled={duplicating}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDuplicateModal} disabled={duplicating}>
            Cancel
          </Button>
          <Button 
            onClick={handleDuplicate} 
            variant="contained" 
            disabled={duplicating || !duplicateName.trim()}
            startIcon={duplicating ? <CircularProgress size={16} /> : <FileCopyIcon />}
          >
            {duplicating ? 'Duplicating...' : 'Duplicate Project'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Settings Dialog */}
      <Dialog open={showTemplateSettingsDialog} onClose={handleCloseTemplateSettings} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Flow Template</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Template Name"
              value={templateSettings.name}
              onChange={(e) => setTemplateSettings(prev => ({ ...prev, name: e.target.value }))}
              required
              fullWidth
              disabled={savingTemplateSettings}
            />
            <TextField
              label="Description"
              value={templateSettings.description}
              onChange={(e) => setTemplateSettings(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={3}
              fullWidth
              disabled={savingTemplateSettings}
            />
            <TextField
              select
              label="Category"
              value={templateSettings.category}
              onChange={(e) => setTemplateSettings(prev => ({ ...prev, category: e.target.value }))}
              fullWidth
              disabled={savingTemplateSettings}
            >
              <MenuItem value="invoice">Invoice</MenuItem>
              <MenuItem value="receipt">Receipt</MenuItem>
              <MenuItem value="contract">Contract</MenuItem>
              <MenuItem value="form">Form</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
            <TextField
              label="Tags"
              value={templateSettings.tags}
              onChange={(e) => setTemplateSettings(prev => ({ ...prev, tags: e.target.value }))}
              helperText="Comma-separated tags"
              fullWidth
              disabled={savingTemplateSettings}
            />
            <TextField
              select
              label="Availability"
              value={templateSettings.availability}
              onChange={(e) => setTemplateSettings(prev => ({
                ...prev,
                availability: e.target.value,
                sharedCompanyIds: e.target.value === 'selected_companies' ? prev.sharedCompanyIds : []
              }))}
              fullWidth
              disabled={savingTemplateSettings}
            >
              <MenuItem value="current_company">Source company only</MenuItem>
              <MenuItem value="all_companies">All companies</MenuItem>
              <MenuItem value="selected_companies">Selected companies</MenuItem>
            </TextField>
            {templateSettings.availability === 'selected_companies' && (
              <TextField
                select
                SelectProps={{ multiple: true }}
                label="Companies"
                value={templateSettings.sharedCompanyIds}
                onChange={(e) => setTemplateSettings(prev => ({
                  ...prev,
                  sharedCompanyIds: e.target.value
                }))}
                helperText="Choose which companies can use this template"
                fullWidth
                disabled={savingTemplateSettings}
              >
                {allCompanies.map(companyOption => (
                  <MenuItem key={companyOption._id} value={companyOption._id}>
                    {companyOption.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTemplateSettings} disabled={savingTemplateSettings}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveTemplateSettings}
            variant="contained"
            disabled={savingTemplateSettings || !templateSettings.name.trim()}
          >
            {savingTemplateSettings ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Company Sync Dialog */}
      <SyncDialog
        open={showSyncDialog}
        onClose={() => setShowSyncDialog(false)}
        syncType="company"
        targetId={companyId}
        targetName={company?.name}
        onSyncComplete={handleSyncComplete}
      />

      {/* Company Settings Dialog */}
      <Dialog open={showSettingsDialog} onClose={handleCloseSettings} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SettingsIcon />
            Company Settings
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} pt={2}>
            <TextField
              label="Company Name"
              value={settingsName}
              onChange={(e) => setSettingsName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={settingsDescription}
              onChange={(e) => setSettingsDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              select
              label="Industry"
              value={settingsIndustry}
              onChange={(e) => setSettingsIndustry(e.target.value)}
              fullWidth
            >
              {industries.map((ind) => (
                <MenuItem key={ind} value={ind}>
                  {ind}
                </MenuItem>
              ))}
            </TextField>
            <Box>
              <Typography variant="subtitle2" mb={1} display="flex" alignItems="center" gap={1}>
                <ColorLensIcon fontSize="small" />
                Brand Colors
              </Typography>
              
              {/* Primary Color */}
              <Box display="flex" gap={2} alignItems="center" mb={2}>
                <TextField
                  type="color"
                  value={settingsPrimaryColor}
                  onChange={(e) => setSettingsPrimaryColor(e.target.value)}
                  sx={{ width: 80 }}
                />
                <TextField
                  label="Primary Color"
                  value={settingsPrimaryColor}
                  onChange={(e) => setSettingsPrimaryColor(e.target.value)}
                  fullWidth
                  helperText="Headers, navigation, company identity"
                />
              </Box>
              
              {/* Action Color */}
              <Box display="flex" gap={2} alignItems="center" mb={2}>
                <TextField
                  type="color"
                  value={useMonochrome ? settingsPrimaryColor : settingsActionColor}
                  onChange={(e) => {
                    setSettingsActionColor(e.target.value);
                    setUseMonochrome(false);
                  }}
                  disabled={useMonochrome}
                  sx={{ width: 80 }}
                />
                <TextField
                  label="Action Button Color"
                  value={useMonochrome ? settingsPrimaryColor : settingsActionColor}
                  onChange={(e) => {
                    setSettingsActionColor(e.target.value);
                    setUseMonochrome(false);
                  }}
                  disabled={useMonochrome}
                  fullWidth
                  helperText="Start buttons, important CTAs"
                />
              </Box>
              
              {/* Monochrome Checkbox */}
              <Box display="flex" alignItems="center">
                <input
                  type="checkbox"
                  id="use-monochrome"
                  checked={useMonochrome}
                  onChange={(e) => setUseMonochrome(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                <label htmlFor="use-monochrome" style={{ cursor: 'pointer', fontSize: '0.875rem' }}>
                  Use primary color for all actions (recommended)
                </label>
              </Box>
            </Box>
            <Divider />
            <Box display="flex" justifyContent="flex-end">
              <Button
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  setShowSettingsDialog(false);
                  handleDeleteCompany();
                }}
              >
                Delete Company
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSettings}>Cancel</Button>
          <Button 
            onClick={handleSaveSettings} 
            variant="contained" 
            disabled={savingSettings || !settingsName.trim()}
            startIcon={savingSettings ? <CircularProgress size={16} /> : <SettingsIcon />}
          >
            {savingSettings ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default DashboardPage; 